// Shared SRT/VTT parsing & formatting helpers.
// Pure functions, browser-safe, no dependencies.

export interface Cue {
  index: number;
  startMs: number;
  endMs: number;
  text: string; // may contain \n between lines
}

const TS_RE =
  /(\d{1,2}):(\d{2}):(\d{2})[.,](\d{1,3})\s*-->\s*(\d{1,2}):(\d{2}):(\d{2})[.,](\d{1,3})/;

function tsToMs(h: string, m: string, s: string, ms: string): number {
  const millis = (ms + "000").slice(0, 3); // pad
  return (
    parseInt(h, 10) * 3_600_000 +
    parseInt(m, 10) * 60_000 +
    parseInt(s, 10) * 1_000 +
    parseInt(millis, 10)
  );
}

export function msToSrt(ms: number): string {
  const clamped = Math.max(0, Math.round(ms));
  const h = Math.floor(clamped / 3_600_000);
  const m = Math.floor((clamped % 3_600_000) / 60_000);
  const s = Math.floor((clamped % 60_000) / 1_000);
  const milli = clamped % 1_000;
  return (
    String(h).padStart(2, "0") +
    ":" +
    String(m).padStart(2, "0") +
    ":" +
    String(s).padStart(2, "0") +
    "," +
    String(milli).padStart(3, "0")
  );
}

export function msToVtt(ms: number): string {
  return msToSrt(ms).replace(",", ".");
}

/**
 * Parse SRT or VTT into cues. Tolerates BOM, CRLF, missing trailing
 * blank line, and WEBVTT header. Skips invalid blocks silently.
 */
export function parseCaptions(input: string): Cue[] {
  const cleaned = input
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/^WEBVTT.*?(\n\n|$)/, "")
    .trim();

  if (!cleaned) return [];

  const blocks = cleaned.split(/\n{2,}/);
  const cues: Cue[] = [];
  let auto = 1;

  for (const block of blocks) {
    const lines = block.split("\n").filter((l) => l.length > 0 || true);
    if (lines.length === 0) continue;

    let i = 0;
    // Optional cue identifier (number or name) on first line.
    let idx = auto;
    if (!TS_RE.test(lines[i]) && lines[i + 1] && TS_RE.test(lines[i + 1])) {
      const maybeNum = parseInt(lines[i].trim(), 10);
      if (Number.isFinite(maybeNum)) idx = maybeNum;
      i++;
    }

    const tsLine = lines[i];
    if (!tsLine) continue;
    const m = TS_RE.exec(tsLine);
    if (!m) continue;

    const startMs = tsToMs(m[1], m[2], m[3], m[4]);
    const endMs = tsToMs(m[5], m[6], m[7], m[8]);
    const text = lines
      .slice(i + 1)
      .join("\n")
      // Strip VTT cue settings, voice tags and basic HTML.
      .replace(/<\/?[bivu]>/gi, "")
      .replace(/<v[^>]*>/gi, "")
      .replace(/<\/v>/gi, "")
      .trim();

    if (!text) continue;
    cues.push({ index: idx, startMs, endMs, text });
    auto = idx + 1;
  }

  return cues;
}

export function cuesToSrt(cues: Cue[]): string {
  return (
    cues
      .map(
        (c, i) =>
          `${i + 1}\n${msToSrt(c.startMs)} --> ${msToSrt(c.endMs)}\n${c.text}`,
      )
      .join("\n\n") + (cues.length ? "\n" : "")
  );
}

export function cuesToVtt(cues: Cue[]): string {
  const body = cues
    .map(
      (c) => `${msToVtt(c.startMs)} --> ${msToVtt(c.endMs)}\n${c.text}`,
    )
    .join("\n\n");
  return `WEBVTT\n\n${body}${cues.length ? "\n" : ""}`;
}

export function shiftCues(cues: Cue[], offsetMs: number): Cue[] {
  return cues.map((c) => ({
    ...c,
    startMs: Math.max(0, c.startMs + offsetMs),
    endMs: Math.max(0, c.endMs + offsetMs),
  }));
}

/**
 * Merge multiple SRT segments. If `gapMs` > 0, each subsequent file is
 * appended after `previousEnd + gapMs` so cues never overlap.
 */
export function mergeCues(parts: Cue[][], gapMs = 0): Cue[] {
  const out: Cue[] = [];
  let offset = 0;
  for (const part of parts) {
    if (part.length === 0) continue;
    const partMin = Math.min(...part.map((c) => c.startMs));
    const shift = offset === 0 ? 0 : offset - partMin + gapMs;
    for (const c of part) {
      out.push({
        index: 0,
        startMs: c.startMs + shift,
        endMs: c.endMs + shift,
        text: c.text,
      });
    }
    offset = Math.max(...out.map((c) => c.endMs));
  }
  return out;
}

/**
 * Split cues that exceed `maxLines` lines or `maxChars` per line.
 * Long cues are word-wrapped and their duration is divided proportionally
 * by character count between the resulting child cues.
 */
export function splitCues(
  cues: Cue[],
  maxChars = 42,
  maxLines = 2,
): Cue[] {
  const out: Cue[] = [];
  for (const cue of cues) {
    const words = cue.text.replace(/\s+/g, " ").trim().split(" ");
    const lines: string[] = [];
    let current = "";
    for (const w of words) {
      if (!current) current = w;
      else if ((current + " " + w).length <= maxChars) current += " " + w;
      else {
        lines.push(current);
        current = w;
      }
    }
    if (current) lines.push(current);

    if (lines.length <= maxLines) {
      out.push({ ...cue, text: lines.join("\n") });
      continue;
    }

    // Group lines into chunks of maxLines and proportion duration by chars.
    const chunks: string[][] = [];
    for (let i = 0; i < lines.length; i += maxLines) {
      chunks.push(lines.slice(i, i + maxLines));
    }
    const totalChars = chunks.reduce(
      (n, c) => n + c.join(" ").length,
      0,
    ) || 1;
    const total = cue.endMs - cue.startMs;
    let cursor = cue.startMs;
    for (const chunk of chunks) {
      const portion = (chunk.join(" ").length / totalChars) * total;
      const start = cursor;
      const end = Math.min(cue.endMs, Math.round(cursor + portion));
      cursor = end;
      out.push({
        index: 0,
        startMs: start,
        endMs: end,
        text: chunk.join("\n"),
      });
    }
  }
  return out;
}

export function cuesToPlainText(cues: Cue[]): string {
  // Join lines inside a cue with a space, separate cues with a single
  // newline; group cues into paragraphs on sentence-ending punctuation.
  const flat = cues.map((c) => c.text.replace(/\s*\n\s*/g, " ").trim());
  const paragraphs: string[] = [];
  let buf: string[] = [];
  for (const line of flat) {
    buf.push(line);
    if (/[.!?]["')\]]?$/.test(line)) {
      paragraphs.push(buf.join(" "));
      buf = [];
    }
  }
  if (buf.length) paragraphs.push(buf.join(" "));
  return paragraphs.join("\n\n");
}

/**
 * Generate cues from a plain transcript at a target words-per-minute
 * reading speed. Sentences are kept as a single cue when they fit
 * within `maxChars`; longer sentences are word-wrapped into adjacent cues.
 */
export function transcriptToCues(
  text: string,
  wpm = 160,
  maxChars = 84,
): Cue[] {
  const msPerWord = (60 * 1000) / Math.max(60, wpm);
  const sentences = text
    .replace(/\s+/g, " ")
    .trim()
    .split(/(?<=[.!?])\s+(?=[A-Z"'(\[])/);
  const cues: Cue[] = [];
  let cursor = 0;

  for (const sentence of sentences) {
    if (!sentence) continue;
    const pieces: string[] = [];
    if (sentence.length <= maxChars) {
      pieces.push(sentence);
    } else {
      const words = sentence.split(" ");
      let current = "";
      for (const w of words) {
        if (!current) current = w;
        else if ((current + " " + w).length <= maxChars) current += " " + w;
        else {
          pieces.push(current);
          current = w;
        }
      }
      if (current) pieces.push(current);
    }
    for (const piece of pieces) {
      const words = piece.split(/\s+/).filter(Boolean).length;
      const dur = Math.max(1200, Math.round(words * msPerWord));
      cues.push({
        index: 0,
        startMs: cursor,
        endMs: cursor + dur,
        text: piece,
      });
      cursor += dur + 80; // tiny inter-cue gap
    }
  }
  return cues;
}

/**
 * Pick the active cue for a given playback time (in seconds).
 */
export function activeCueAt(cues: Cue[], seconds: number): Cue | null {
  const ms = seconds * 1000;
  for (const c of cues) {
    if (ms >= c.startMs && ms <= c.endMs) return c;
  }
  return null;
}

export function extractYouTubeId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/^[\w-]{11}$/.test(trimmed)) return trimmed;
  const m = trimmed.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{11})/,
  );
  return m ? m[1] : null;
}
